import { PackageCheck } from 'lucide-react';
import type { BestellungFortschritt } from '@/types/dashboard';

/**
 * „2 von 3 da" — wie weit ist diese Bestellung? (A57.1, #123)
 *
 * DER BEFUND
 *   Eine Bestellung mit drei Titeln, von denen zwei da sind, sah aus wie
 *   eine, von der nichts da ist. Der Zustand folgt der langsamsten
 *   Position — richtig, aber die schnelleren waren nirgends abzulesen.
 *
 * DIE ZAHL WIRD HIER NICHT GERECHNET
 *   `fortschritt` kommt fertig vom Server (api/lib/fortschritt.php) und
 *   ist `null`, wenn es nichts zu sagen gibt. Diese Marke prüft deshalb
 *   NUR auf `null` — sie kennt weder die Untergrenze noch die Regel für
 *   den Endzustand. Wüsste sie sie, stünde die Regel zweimal da, und
 *   zwei Stellen laufen auseinander (Fallstrick 15, der Verlags-Fall).
 *
 * WARUM „da" UND NICHT „raus"
 *   Der Betreiber hat die Form genannt („2 von 3 raus"), nicht den
 *   Inhalt. Gezählt wird das Eintreffen — die Begründung steht dort, wo
 *   die Zahl entsteht: im Kopf von api/lib/fortschritt.php.
 *
 * WARUM KEIN BALKEN
 *   Ein Fortschrittsbalken wäre auf 390 Pixeln entweder zu schmal zum
 *   Ablesen oder zu breit für die Zeile, und er sagt „ungefähr zwei
 *   Drittel", wo die genaue Zahl kürzer und genauer ist. Die Ziffern
 *   stehen in `tabular-nums`, damit sie in der Tabelle untereinander
 *   fluchten.
 *
 * Teal statt Gold: Das hier ist Fortschritt, keine offene Arbeit. Die
 * goldene Marke daneben („3 offen") meint etwas anderes — noch keinem
 * Beschaffungsweg zugeordnet —, und zwei gleichfarbige Marken mit
 * verschiedener Bedeutung wären genau die Verwechslung, die ein Brett
 * unbrauchbar macht.
 */
export function FortschrittBadge({
  fortschritt
}: {
  fortschritt: BestellungFortschritt | null;
}) {
  // `!fortschritt` und nicht `=== null`. Der Übersetzer kennt nur null,
  // aber eine Antwort kann das Feld auch gar nicht führen — ein alter
  // Eintrag im ServiceWorker-Zwischenspeicher, eine Antwort von einem
  // Server, der noch nicht ausgerollt ist. Fehlend heißt genau dasselbe
  // wie null: hier gibt es nichts zu sagen. Ohne diese Zeile stürzte
  // beim Zerlegen unten die ganze Detailansicht ab — gemessen an zwei
  // Prüfdateien, deren Antwort-Vorlagen das Feld noch nicht führen.
  if (!fortschritt) return null;
  const { da, gesamt } = fortschritt;

  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded bg-teal-soft px-1.5 py-0.5 text-xs tabular-nums text-teal"
      title={`${da} von ${gesamt} bestellten Exemplaren sind eingetroffen — ${fortschritt.offen} fehlen noch.`}
    >
      <PackageCheck aria-hidden className="h-3 w-3 shrink-0" />
      {da} von {gesamt} da
    </span>
  );
}
